import type { QueryClient, QueryKey } from "@tanstack/react-query";

export type WorkflowObservabilityAdvanceResponse = {
  state: "complete" | "warming" | "busy";
  retryAfterSeconds?: number;
  statusCode: 200 | 202 | 409;
};

export function parseWorkflowObservabilityAdvanceResponse(
  statusCode: number,
  body: unknown,
): WorkflowObservabilityAdvanceResponse {
  if (body === null || typeof body !== "object") {
    throw new Error("Invalid workflow observability advance response");
  }
  const response = body as { state?: unknown; retryAfterSeconds?: unknown };
  const expectedState = statusCode === 200 ? "complete" : statusCode === 202 ? "warming" : statusCode === 409 ? "busy" : undefined;
  if (!expectedState || response.state !== expectedState) {
    throw new Error(`Unexpected workflow observability advance response: ${statusCode}`);
  }
  const retryAfterSeconds = typeof response.retryAfterSeconds === "number"
    && Number.isFinite(response.retryAfterSeconds)
    && response.retryAfterSeconds > 0
    ? response.retryAfterSeconds
    : undefined;
  return {
    state: expectedState,
    ...(retryAfterSeconds === undefined ? {} : { retryAfterSeconds }),
    statusCode: statusCode as WorkflowObservabilityAdvanceResponse["statusCode"],
  };
}

export function workflowObservabilityAdvancePath(runId: string, source: string): string {
  return `/runs/${encodeURIComponent(runId)}/observability/advance?source=${encodeURIComponent(source)}`;
}

export function workflowStepsWarming(
  page: { status?: "warming" | "ready"; coverage?: "warming" | "ready" } | undefined,
): boolean {
  return page?.status === "warming" || page?.coverage === "warming";
}

type AdvanceState = { inFlight: boolean; retryAt: number };

/** Coordinates projection advances across all step queries for one run in this tab. */
export class WorkflowObservabilityAdvanceGate {
  private readonly states = new Map<string, AdvanceState>();
  private readonly now: () => number;

  constructor(now: () => number = Date.now) {
    this.now = now;
  }

  isEligible(key: string, warming: boolean): boolean {
    if (!warming) return false;
    const now = this.now();
    this.prune(now);
    const state = this.states.get(key);
    return !state?.inFlight && (!state || state.retryAt <= now);
  }

  retryAfterSeconds(key: string): number | undefined {
    const retryAt = this.states.get(key)?.retryAt;
    if (retryAt === undefined) return undefined;
    const remainingMs = retryAt - this.now();
    return remainingMs > 0 ? Math.ceil(remainingMs / 1_000) : undefined;
  }

  start(
    key: string,
    request: () => Promise<WorkflowObservabilityAdvanceResponse>,
  ): Promise<WorkflowObservabilityAdvanceResponse> | undefined {
    if (!this.isEligible(key, true)) return undefined;
    const state = this.states.get(key) ?? { inFlight: false, retryAt: 0 };
    state.inFlight = true;
    this.states.set(key, state);

    return Promise.resolve()
      .then(request)
      .then((response) => {
        const delay = Math.max(1, response.retryAfterSeconds ?? 0);
        state.retryAt = this.now() + delay * 1_000;
        return response;
      }, (error: unknown) => {
        state.retryAt = this.now() + 5_000;
        throw error;
      })
      .finally(() => {
        state.inFlight = false;
      });
  }

  private prune(now: number): void {
    for (const [key, state] of this.states) {
      if (!state.inFlight && state.retryAt <= now) this.states.delete(key);
    }
  }
}

export async function invalidateWorkflowStepQueries(
  client: QueryClient,
  queryKeys: readonly QueryKey[],
): Promise<void> {
  await Promise.all(queryKeys.map((queryKey) => client.invalidateQueries({ queryKey, exact: true })));
}
